import React, { useState } from "react";
import { fetchMachineList, fetchMachineData ,patchMachineData } from "../../fetchers/machine";
import { useQuery } from "@tanstack/react-query";
import MachinesGrid from "./components/machinesGrid";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import Popup from "../../components/Popup";
import MachineSettingsDialog from "../machineDetails/components/machineSettingsDialog";
import MachineSections from "../machineSections";
import { FormattedMessage } from "react-intl";
import messages from "./components/messages";
import machineSectionsMessages from "../machineSections/components/messages";
import { getStopReasonCodes } from "../stopReasonCodes/hooks/fetchers";
import { fetchAreas } from "../../fetchers/area";
import { fetchMachineTypes } from "../../fetchers/machineType";
import AxionControls from "../../components/controls/AxionControls";
import { pushErrorMessage } from '../../utils/notifications';
import { fetchSpeedUnitTypes } from '../../fetchers/speedUnitTypes';
import useFeatureFlags from "../../hooks/useFeatureFlags";

export default function Machines(props) {
    const [ companyKey, setCompanyKey] = useLocalStorage("companyKey", 1);
    const [openSettingsPopup, setOpenSettingsPopup] = useState(false);
    const [machineSettings, setMachineSettings] = useState({});
    const [openSectionsPopup, setOpenSectionsPopup] = useState(false);
    const [sectionsMachineId, setSectionsMachineId] = useState(null);
    const [showBackdrop, setShowBackdrop] = useState(false);
    const featuresQuery = useFeatureFlags();

    const machinesQuery = useQuery({
        queryKey: ['machinelist', companyKey], 
        queryFn: () => fetchMachineList(companyKey),
        staleTime: 60000,
        refetchInterval: 60000,
        keepPreviousData: true
    })

    const stopReasonCodesQuery = useQuery(
        ["stopReasonCodes", companyKey],
        () => getStopReasonCodes(companyKey),
        {
            keepPreviousData: true
        });
    
    const aresQuery = useQuery(
        ["areas"],
        () => fetchAreas(),
        {
            staleTime: 60000,
            refetchInterval: 60000,
            keepPreviousData: true
        });
    
    const machineTypes = useQuery(
        ["machineTypes"],
        () => fetchMachineTypes(),
        {
            keepPreviousData: true
        });

    const speedUnitTypesQuery = useQuery(
        ["speedUnitTypes", true],
        () => fetchSpeedUnitTypes(true),
        {
            staleTime: 60000,
            refetchInterval: 60000,
            keepPreviousData: true
        });  

    const handleEditSettingsClick = (id) => {
        fetchMachineData(id).then((machine) => {
            setMachineSettings(machine);
            setOpenSettingsPopup(true);
        });
    }

    const handleSettingsSave = () => {
        setShowBackdrop(true);
        patchMachineData(machineSettings).then(() => {
            setOpenSettingsPopup(false);
            machinesQuery.refetch();
            setShowBackdrop(false);
        })
            .catch((error) => {
            console.log(error);
            setShowBackdrop(false);
            pushErrorMessage(`Could not update the machine settings.`);
        });
    }
    
    const handleSettingsCancel = () => {
        setOpenSettingsPopup(false);
    }

    const handleEditMachineSectionsClick = (id) => {
        setSectionsMachineId(id);
        setOpenSectionsPopup(true);
    }

    if(machinesQuery?.isLoading || stopReasonCodesQuery.isLoading || aresQuery.isLoading || machineTypes.isLoading || speedUnitTypesQuery.isLoading) {
        return <AxionControls.AxionBackdrop open={true} />
    }

    return (
    <>
        <MachinesGrid 
            machineResult={machinesQuery} 
            handleEditSettingsClick={handleEditSettingsClick}
            handleEditMachineSectionsClick={handleEditMachineSectionsClick}
            />
        <Popup
          openPopup={openSettingsPopup}
          setOpenPopup={setOpenSettingsPopup}
          title={<FormattedMessage {...messages.editSettings} />}>
          <MachineSettingsDialog
            setOpenPopup={setOpenSettingsPopup}
            stopReasonCodes={stopReasonCodesQuery.data}
            machineData={machineSettings}
            areas={aresQuery.data}
            machineTypes={machineTypes.data}
            featuresQuery={featuresQuery}
            speedUnitTypes={speedUnitTypesQuery.data}
            setMachineSettings={setMachineSettings}
            onSettingsSave={handleSettingsSave}
            onSettingsCancel={handleSettingsCancel} />
        </Popup>
        <Popup
          openPopup={openSectionsPopup}
          setOpenPopup={setOpenSectionsPopup}
          maxWidth="lg"
          title={<FormattedMessage {...machineSectionsMessages.editMachineSectionsButtonLabel} />}>
          <MachineSections
            machineId={sectionsMachineId}
            stopReasonCodes={stopReasonCodesQuery.data} />
        </Popup>
        <AxionControls.AxionBackdrop open={showBackdrop} />
    </>);
}
